import { Timestamp } from "firebase/firestore";
import { t } from "i18next";
import ReactDatePicker from "react-datepicker";

import { Utilities } from "../../../../../utils/utilities";
import { CustomFieldDataOptions } from "../../../Property/Collections/PropertyCustomFieldData";
import { CustomFieldDataTypeHelper, DataEditorContext } from "./CustomFieldDataTypeHelper";
import styles from "./CustomFieldDateDataType.module.css";

export class CustomFieldDateDataType extends CustomFieldDataTypeHelper<Date | string> {
  static getDateFormat(options: CustomFieldDataOptions): string[] {
    let dateTexts = options.dateEnabled ? ["dd.MM.yyyy", "dd/MM/yyyy"] : [""];
    if (options.timeEnabled) dateTexts = dateTexts.map((text) => text + (text.length > 0 ? ", " : "") + " hh.mm");
    return dateTexts;
  }
  static getDateData(data: Date | undefined, options: CustomFieldDataOptions): Timestamp | string | undefined {
    if (!data) return undefined;
    if (options.dateEnabled && options.timeEnabled) return Timestamp.fromDate(data);
    if (options.dateEnabled && !options.timeEnabled) return Utilities.formatDate(data, "yyyy.mm.dd");
    if (!options.dateEnabled && options.timeEnabled) return Utilities.formatDate(data, "hh:MM");
    return undefined;
  }
  static getDateFieldAsDate(data: any, options: CustomFieldDataOptions): Date | undefined {
    if (typeof data === "string") {
      // Check for ISO first
      if (data.indexOf("T") > 0) {
        return new Date(data);
      }

      const dateEnabled = options.dateEnabled || true;
      const timeEnabled = options.timeEnabled || false;

      const separator = data.includes(".") ? "." : data.includes("/") ? "/" : data.includes(":") ? ":" : "";
      const elements = data.split(separator).map((str) => parseInt(str));
      if (dateEnabled && !timeEnabled) {
        return new Date(elements[0], elements[1] - 1, elements[2]);
      }
      if (!dateEnabled && timeEnabled) {
        const date = new Date();
        date.setHours(elements[0]);
        date.setMinutes(elements[1]);
        return date;
      }
      const date = new Date(data);
      if (!isNaN(date.getTime())) {
        return date;
      }
    } else if (data instanceof Timestamp) {
      return data.toDate();
    } else if (data instanceof Date) {
      return data;
    }
    return undefined;
  }

  getDataEditor(
    data: any | undefined,
    options: CustomFieldDataOptions,
    context: DataEditorContext,
    setData: (data: any | undefined) => void
  ): JSX.Element {
    return (
      <ReactDatePicker
        selected={CustomFieldDateDataType.getDateFieldAsDate(data, options)}
        onChange={(date) => setData(CustomFieldDateDataType.getDateData(date instanceof Date ? date : undefined, options))}
        timeIntervals={30}
        dateFormat={CustomFieldDateDataType.getDateFormat(options)}
        showTimeSelect={options.timeEnabled}
        placeholderText={t("main.date")}
        className={styles.datePicker}
        showTimeSelectOnly={options.timeEnabled && !options.dateEnabled}
        showYearDropdown={options.dateEnabled}
        yearDropdownItemNumber={100}
        scrollableYearDropdown={options.dateEnabled}
      />
    );
  }

  getStringRepresentation(data: any, options: CustomFieldDataOptions, context: DataEditorContext): string {
    const date = CustomFieldDateDataType.getDateFieldAsDate(data, options);

    let dateFormat: Intl.DateTimeFormatOptions = {
      month: options.dateEnabled ? "short" : undefined,
      year: options.dateEnabled ? "numeric" : undefined,
      day: options.dateEnabled ? "numeric" : undefined,
      hour: options.timeEnabled ? "2-digit" : undefined,
      minute: options.timeEnabled ? "2-digit" : undefined,
    };

    return date?.toLocaleString("nb-NO", dateFormat) ?? "";
  }
}
